<template>
  <WdsStack direction-row justify-start align-start gap-sm>
    <div>
      <WdsInputCurrency
        v-model="rate"
        :config="companyCurrencyConfig"
        class="tw-w-[140px]"
        :error="allowWarnings && showMinimumRateWarning ? minimumRateWarningText : undefined"
        data-test="rate-input"
        :disabled
        @focus="focusedRateInput = Rate_Input.RATE"
      />

      <div v-if="hasMultipleCurrencies && enableMultipleCurrencies" class="tw-w-[140px]">
        <WdsButtonGhost
          secondary
          class="tw-text-left"
          data-test="prefer-worker-currency-button"
          @click.prevent="togglePreferWorkerCurrency"
        >
          {{
            !preferWorkerCurrency
              ? $t(`Change to worker’s preferred currency`)
              : `${$t('Revert to')} ${companyCurrency}`
          }}
        </WdsButtonGhost>
      </div>
    </div>

    <WdsIcon
      v-if="preferWorkerCurrency && enableMultipleCurrencies"
      class="tw-mt-2 tw-text-gray-600 tw-flex-shrink-0"
      name="arrow-right"
      :size="20"
    />

    <div v-if="preferWorkerCurrency && enableMultipleCurrencies" class="">
      <WdsInputCurrency
        v-model="rateExchanged"
        :config="workerCurrencyConfig"
        class="tw-w-[140px] tw-max-w-max !tw-flex-grow-0"
        :error="allowWarnings && showMinimumRateWarning ? minimumRateWarningText : undefined"
        data-test="rate-exchanged-input"
        :disabled
        @focus="focusedRateInput = Rate_Input.RATE_EXCHANGED"
      />

      <WdsText v-if="preferWorkerCurrency && enableMultipleCurrencies" secondary>
        {{ $t('The amount the worker will receive, based on today’s exchange rate') }}
      </WdsText>
    </div>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  WdsStack,
  WdsText,
  WdsButtonGhost,
  WdsInputCurrency,
  WdsIcon,
} from '@worksome/wds-components'

import { RateType } from '@/enums'
import { RateType as GQLRateType } from '@/gql/graphql'
import { $t } from '@/composables/translation'

const emit = defineEmits(['toggle:preferCurrency', 'update:rate', 'update:rateExchanged'])

// @ts-ignore
const { exchangeRates } = window

// @ts-ignore
const { thousandsSeparator, decimalSeparator } = window.currentLocale

const props = withDefaults(
  defineProps<{
    enableMultipleCurrencies?: boolean
    initialRate?: string
    initialRateExchanged?: string
    initialPreferWorkerCurrency?: boolean
    rateType?: RateType | GQLRateType | null
    companyCurrency: string
    workerCurrency?: string | null
    minimumRate?: string | number
    allowWarnings?: boolean
    showMinimumRateWarning?: boolean
    disabled?: boolean
  }>(),
  {
    enableMultipleCurrencies: false,
    initialRate: '',
    initialRateExchanged: '',
    initialPreferWorkerCurrency: false,
    rateType: null,
    workerCurrency: null,
    minimumRate: '',
    allowWarnings: false,
    showMinimumRateWarning: false,
    disabled: false,
  },
)

enum Rate_Input {
  RATE = 'rate',
  RATE_EXCHANGED = 'rateExchanged',
}

const focusedRateInput = ref<Rate_Input | null>(null)
const rate = ref<string>(props.initialRate)
const rateExchanged = ref<string>(props.initialRateExchanged)
const preferWorkerCurrency = ref<boolean>(props.initialPreferWorkerCurrency)

const companyCurrencyConfig = computed(() => ({
  currencySymbol: props.companyCurrency,
  thousandsSeparator,
  decimalSeparator,
  showFractions: true,
}))

const workerCurrencyConfig = computed(() => ({
  currencySymbol: props.workerCurrency,
  thousandsSeparator,
  decimalSeparator,
  showFractions: true,
}))

const hasMultipleCurrencies = computed(() => props.companyCurrency !== props.workerCurrency)

const minimumRateWarningText = computed(() => {
  const type = props.rateType === RateType.FIXED ? 'amount' : 'rate'

  const currency = preferWorkerCurrency.value ? props.workerCurrency : props.companyCurrency

  return `Minimum ${props.rateType} ${type} is ${currency} ${props.minimumRate}`
})

const togglePreferWorkerCurrency = () => {
  preferWorkerCurrency.value = !preferWorkerCurrency.value
  emit('toggle:preferCurrency', preferWorkerCurrency.value)
}

const exchangeCurrencies = (amount: string, fromCurrency, toCurrency) =>
  (
    Number(amount) *
    (Number(exchangeRates[toCurrency]) / Number(exchangeRates[fromCurrency]))
  ).toFixed(2)

const rateToExchanged = () => {
  if (preferWorkerCurrency.value === true) {
    rateExchanged.value = exchangeCurrencies(
      rate.value,
      props.companyCurrency,
      props.workerCurrency,
    )
  }
}

const exchangedToRate = () => {
  rate.value = exchangeCurrencies(rateExchanged.value, props.workerCurrency, props.companyCurrency)
}

watch(
  () => preferWorkerCurrency.value,
  (value: boolean) => {
    if (value) {
      rateToExchanged()
    }
  },
)

watch(
  () => rate.value,
  (value: string) => {
    emit('update:rate', value)

    // If we are disabled it means we are being controlled externally in that case changes from our prop watch need
    // to be exchanged
    if (focusedRateInput.value === Rate_Input.RATE || props.disabled) {
      rateToExchanged()
    }
  },
)

watch(
  () => props.initialRate,
  (value: string) => {
    rate.value = value
  },
)

watch(
  () => props.initialRateExchanged,
  (value: string) => {
    rateExchanged.value = value
  },
)

watch(
  () => rateExchanged.value,
  (value: string) => {
    emit('update:rateExchanged', value)

    // If we are disabled it means we are being controlled externally in that case changes from our prop watch need
    // to be exchanged
    if (focusedRateInput.value === Rate_Input.RATE_EXCHANGED || props.disabled) {
      exchangedToRate()
    }
  },
)

onMounted(() => {
  if (rate.value === '' && rateExchanged.value !== '') {
    exchangedToRate()
  } else if (rate.value !== '' && rateExchanged.value === '') {
    rateToExchanged()
  }
})
</script>
